import cn from 'classnames'
import { ONBOARDING_STEPS } from '@forwardflow/constants/onboarding.constants'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import { Icon } from '../ui/Icon'
import { leadsFor } from '../../utils/budget'
import {
   fullName,
   membersOf,
   ownerOf,
   seatCostFor,
   seatWords,
} from '../../utils/seats'
import { prettyPhone } from '../../utils/format'
import { chosenFor, STOP_QUESTIONS } from '../../utils/stops'
import { FF_ASSISTANT_NUMBER_PRETTY } from '../../utils/constants'
import { BUSINESS_FIELDS } from '../steps/business-fields'

interface IRow {
   step: TOnboardingStep
   title: string
   icon: string
   value?: string
   sub?: string
}

/**
 * The left panel: one row per step, an icon in a tinted circle, the title,
 * and what was decided. Done rows show a check and reopen on click; the
 * current row is lit. Rows the store skips (the card once paid) still show
 * as done, so the count never jumps mid-flow.
 */
export const SetupRail = () => {
   const { onboardingData, getCurrentStep, goToStep, onboardingSaveBlocked } =
      useOnboardingStoreV2()
   const d = onboardingData
   const current = getCurrentStep()
   const owner = ownerOf(d.invitedUsers)
   const seats = 1 + membersOf(d.invitedUsers).length
   const pricing = d.pricingData
   const codes = d.campaignConfiguration?.area_codes || []
   const groups = d.stageMappings || []
   const filled = BUSINESS_FIELDS.filter((f) =>
      String(
         (d.businessDetails as Record<string, unknown> | undefined)?.[f.name] ||
            ''
      ).trim()
   ).length

   const stopRow = (i: number): IRow => {
      const q = STOP_QUESTIONS[i]
      const names = chosenFor(groups, q).map((x) => x.name)
      return {
         step: `stops-${q.id}` as TOnboardingStep,
         title: q.title,
         icon: 'shield',
         value: names.length
            ? names.slice(0, 2).join(', ') +
              (names.length > 2 ? ` +${names.length - 2}` : '')
            : undefined,
      }
   }

   const rows: IRow[] = [
      {
         step: 'fub-connect',
         title: 'Follow Up Boss',
         icon: 'key',
         value: d.isFUBConnected ? 'Connected' : undefined,
      },
      {
         step: 'you',
         title: 'You',
         icon: 'user',
         value: owner ? fullName(owner) : undefined,
         sub: owner?.phone ? prettyPhone(owner.phone) : undefined,
      },
      {
         step: 'your-team',
         title: 'Your team',
         icon: 'users',
         value: owner ? seatWords(seats) : undefined,
         sub: owner ? `$${seatCostFor(seats)}/mo` : undefined,
      },
      {
         step: 'calendar-connection',
         title: 'Calendar',
         icon: 'calendar',
         value: d.isCalendarConnected ? 'Connected' : undefined,
      },
      {
         step: 'budget',
         title: 'Budget',
         icon: 'gauge',
         value: pricing?.maxSpend ? `$${pricing.maxSpend}/mo` : undefined,
         sub: pricing?.maxSpend
            ? `about ${leadsFor(pricing.maxSpend)} leads`
            : undefined,
      },
      {
         step: 'payment',
         title: 'Payment',
         icon: 'card',
         value: d.isSubscriptionActivated ? 'Card on file' : undefined,
      },
      {
         step: 'area-code',
         title: 'Area code',
         icon: 'pin',
         value: codes.length
            ? codes.map((c) => `(${c})`).join(', ')
            : undefined,
      },
      {
         step: 'your-number',
         title: 'Your number',
         icon: 'phone',
         value: FF_ASSISTANT_NUMBER_PRETTY,
      },
      stopRow(0),
      stopRow(1),
      stopRow(2),
      {
         step: 'business-details',
         title: 'Texting approval',
         icon: 'file',
         value: filled
            ? `${filled} of ${BUSINESS_FIELDS.length} filled`
            : undefined,
      },
   ]

   const currentIndex = ONBOARDING_STEPS.indexOf(current)
   return (
      <ol
         className="flex-1 flex flex-col py-2 overflow-y-auto ob-scroll"
         data-testid="ob-rail"
         aria-label="Setup"
      >
         {rows.map((r) => {
            const idx = ONBOARDING_STEPS.indexOf(r.step)
            const state =
               r.step === current
                  ? 'current'
                  : idx < currentIndex
                    ? 'done'
                    : 'todo'
            const inner = (
               <>
                  <span
                     className={cn(
                        'flex shrink-0 items-center justify-center w-9 h-9 rounded-full',
                        state === 'done' &&
                           'bg-[var(--ob-good-tint)] text-[var(--ob-good)]',
                        state === 'current' &&
                           'bg-[var(--ob-primary)] text-white',
                        state === 'todo' &&
                           'bg-[var(--ob-surface-2)] text-[var(--ob-text-3)]'
                     )}
                  >
                     <Icon
                        name={state === 'done' ? 'check' : r.icon}
                        size={16}
                     />
                  </span>
                  <span className="flex-1 min-w-0">
                     <span
                        className={cn(
                           'block text-[14px] font-semibold truncate',
                           state === 'todo'
                              ? 'text-[var(--ob-text-3)]'
                              : 'text-white'
                        )}
                     >
                        {r.title}
                     </span>
                     {state !== 'todo' && r.value ? (
                        <span className="block text-[12px] text-[var(--ob-text-2)] truncate">
                           {r.value}
                           {r.sub ? (
                              <span className="text-[var(--ob-text-3)]">
                                 {' '}
                                 · {r.sub}
                              </span>
                           ) : null}
                        </span>
                     ) : state === 'current' ? (
                        <span className="block text-[12px] text-[var(--ob-light)]">
                           In progress
                        </span>
                     ) : null}
                  </span>
                  {state === 'done' && (
                     <span className="text-[12px] font-semibold text-[var(--ob-light)] opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100">
                        Edit
                     </span>
                  )}
               </>
            )
            const cls = cn(
               'group w-full flex items-center gap-3 text-left px-4 py-2 rounded-xl mx-2 transition-colors',
               state === 'current' && 'bg-[var(--ob-primary-tint)]',
               state === 'done' &&
                  'hover:bg-[var(--ob-surface-2)] cursor-pointer'
            )
            return (
               <li
                  key={r.step}
                  className="flex-1 min-h-[52px] flex items-center pr-4"
                  data-testid={`ob-rail-${r.step}`}
                  data-state={state}
               >
                  {state === 'done' ? (
                     <button
                        type="button"
                        className={cls}
                        onClick={() => goToStep(r.step)}
                        disabled={Boolean(onboardingSaveBlocked)}
                     >
                        {inner}
                     </button>
                  ) : (
                     <div
                        className={cls}
                        aria-current={state === 'current' ? 'step' : undefined}
                     >
                        {inner}
                     </div>
                  )}
               </li>
            )
         })}
      </ol>
   )
}
